import { ArrowUp } from "lucide-react";
import { type FormEvent, type KeyboardEvent, useState } from "react";

import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";

type PlutoComposerProps = {
  /** Blocks a second question while the previous one is still being answered. */
  disabled?: boolean;
  initialValue?: string;
  placeholder?: string;
  onSubmit: (prompt: string) => void;
};

export const PlutoComposer = ({
  disabled = false,
  initialValue = "",
  placeholder = "Ask Pluto to find companies, explain signals, or compare prospects…",
  onSubmit,
}: PlutoComposerProps) => {
  const [prompt, setPrompt] = useState(initialValue);

  const submit = () => {
    if (disabled) return;
    const normalizedPrompt = prompt.trim();
    if (!normalizedPrompt) return;
    onSubmit(normalizedPrompt);
    setPrompt("");
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    submit();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== "Enter" || event.shiftKey || event.nativeEvent.isComposing) return;
    event.preventDefault();
    submit();
  };

  return (
    <form
      className="relative rounded-2xl border border-stone-300 bg-white p-2 shadow-[0_18px_48px_-28px_rgba(28,25,23,0.55)] focus-within:border-stone-500"
      onSubmit={handleSubmit}
    >
      <Textarea
        aria-label="Ask Pluto"
        className="min-h-[96px] resize-none border-0 bg-transparent px-3 pb-12 pt-3 text-base shadow-none focus-visible:ring-0"
        disabled={disabled}
        value={prompt}
        placeholder={placeholder}
        onChange={(event) => setPrompt(event.target.value)}
        onKeyDown={handleKeyDown}
      />
      <div className="absolute inset-x-3 bottom-3 flex items-center justify-between">
        <span className="text-xs text-stone-500">
          {disabled ? "Pluto is still working on this conversation…" : "Enter to send · Shift + Enter for a new line"}
        </span>
        <Button
          aria-label="Send prompt"
          className="h-9 w-9 rounded-full bg-stone-900 p-0 text-white hover:bg-stone-700"
          disabled={disabled || !prompt.trim()}
          type="submit"
        >
          <ArrowUp aria-hidden="true" className="h-4 w-4" />
        </Button>
      </div>
    </form>
  );
};
